import React, { useEffect, useRef, useState } from "react";
import "./ratePlanTable.scss";
import {
  IAccommodationRatePlan,
  IGroupedRatePlanPrices,
} from "pages/secure/facility/accommodation/rates/RatePlans";
import { useTranslation } from "react-i18next";
import {
  faCircleQuestion,
  faEllipsisVertical,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/pro-regular-svg-icons";
import Input from "components/form/input/Input";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import MultipleActionDropdown from "components/multipleActionDropdown/MultipleActionDropdown";
import { IAccommodationUnitType } from "pages/secure/facility/accommodation/unitTypes/UnitTypes";
import { IAccommodationRatePlanPrice } from "api/rpc/2024-04/facilityAdmin/accommodation/ratePlans/ratePlanPrices";

interface IRatePlanTableProps {
  ratePlans: IGroupedRatePlanPrices[];
  unitTypes?: IAccommodationUnitType[];
  startingDate: Date;
  endingDate: Date;
  currentRatePlan: IAccommodationRatePlan;
  onRateChange?: (
    ratePlanId: number,
    unitIndex: number,
    dayIndex: number,
    newValue: number,
    price: IAccommodationRatePlanPrice,
  ) => void;
  handleCloseArrivalChange?: (dayToUpdate: IAccommodationRatePlanPrice) => void;
  handleCloseDepartureChange?: (dayToUpdate: IAccommodationRatePlanPrice) => void;
  handleSetLengthOfStay?: (dayToUpdate: IAccommodationRatePlanPrice) => void;
  handleOpenCloseAllOnArrival?: (plan: IGroupedRatePlanPrices, unit: IUnit) => void;
  handleOpenCloseAllOnDeparture?: (plan: IGroupedRatePlanPrices, unit: IUnit) => void;
  handleOpenSetAllLengthOfStay?: (plan: IGroupedRatePlanPrices, unit: IUnit) => void;
  handleOpenEditAllPrices?: (plan: IGroupedRatePlanPrices, unit: IUnit) => void;
  onDateNavigation?: (direction: "back" | "forward") => void;
}

export interface IUnit {
  unit_type_id: number;
  prices: IAccommodationRatePlanPrice[];
  close_on_arrival?: boolean;
  close_on_departure?: boolean;
  id?: number;
}

const RatePlanTable: React.FC<IRatePlanTableProps> = ({
  ratePlans,
  onRateChange,
  handleCloseArrivalChange,
  handleCloseDepartureChange,
  handleSetLengthOfStay,
  handleOpenEditAllPrices,
  handleOpenCloseAllOnArrival,
  handleOpenCloseAllOnDeparture,
  handleOpenSetAllLengthOfStay,
  startingDate,
  endingDate,
  unitTypes,
  onDateNavigation,
}) => {
  const { t } = useTranslation();

  const [editingCell, setEditingCell] = useState<{
    ratePlanId: number;
    unitIndex: number;
    dayIndex: number;
  } | null>(null);

  const [editedValue, setEditedValue] = useState<string>("");
  const saveTimeout = useRef<NodeJS.Timeout | null>(null);
  const start = startingDate ?? new Date();

  const inputRefs = useRef<Record<string, HTMLInputElement>>({});

  useEffect(() => {
    if (editingCell) {
      const key = `${editingCell.ratePlanId}-${editingCell.dayIndex}`;
      inputRefs.current[key]?.focus({ preventScroll: true });
    }
  }, [editingCell]);

  // const days = React.useMemo(() => {
  //   const result: Date[] = [];

  //   if (!endingDate) {
  //     for (let i = 0; i < 7; i++) {
  //       const nextDay = new Date(start);
  //       nextDay.setDate(start.getDate() + i);
  //       result.push(nextDay);
  //     }
  //     return result;
  //   }

  //   const current = new Date(start);
  //   while (current <= endingDate) {
  //     result.push(new Date(current));
  //     current.setDate(current.getDate() + 1);
  //   }

  //   return result;
  // }, [start, endingDate]);

  const days = React.useMemo(() => {
    const result: Date[] = [];
    for (let i = 0; i < 7; i++) {
      const nextDay = new Date(start);
      nextDay.setDate(start.getDate() + i);
      result.push(nextDay);
    }
    return result;
  }, [start]);

  function handleCloseOnArrival(plan: IGroupedRatePlanPrices, unitIndex: number, dayIndex: number) {
    const dayToUpdate = plan?.units?.[unitIndex]?.prices?.[dayIndex];
    if (dayToUpdate) {
      handleCloseArrivalChange?.(dayToUpdate);
    }
  }

  function handleCloseOnDeparture(plan: IGroupedRatePlanPrices, unitIndex: number, dayIndex: number) {
    const dayToUpdate = plan?.units?.[unitIndex]?.prices?.[dayIndex];
    if (dayToUpdate) {
      handleCloseDepartureChange?.(dayToUpdate);
    }
  }

  function handleSetLengthOfStayOpen(plan: IGroupedRatePlanPrices, unitIndex: number, dayIndex: number) {
    const dayToUpdate = plan?.units?.[unitIndex]?.prices?.[dayIndex];
    if (dayToUpdate) {
      handleSetLengthOfStay?.(dayToUpdate);
    }
  }

  function handleEditAllPrices(plan: IGroupedRatePlanPrices, unit: IUnit) {
    if (plan) {
      handleOpenEditAllPrices?.(plan, unit);
    }
  }

  function handleCloseAllOnArrival(plan: IGroupedRatePlanPrices, unit: IUnit) {
    if (plan) {
      handleOpenCloseAllOnArrival?.(plan, unit);
    }
  }

  function handleCloseAllOnDeparture(plan: IGroupedRatePlanPrices, unit: IUnit) {
    if (plan) {
      handleOpenCloseAllOnDeparture?.(plan, unit);
    }
  }

  function handleSetAllLengthOfStayOpen(plan: IGroupedRatePlanPrices, unit: IUnit) {
    if (plan) {
      handleOpenSetAllLengthOfStay?.(plan, unit);
    }
  }

  return (
    <div className="rate-plan-table-wrapper">
      <table className="rate-plan-table">
        <thead>
          <tr>
            <th>
              <p className="rate-plan-title">
                {t("Rate Plan")} 
                {/* <FontAwesomeIcon icon={faCircleQuestion} className="rate-plan-title-icon" /> */}
              </p>
            </th>
            {days.map((date, index) => (
              <th key={index}>
                <div className="flex flex-row items-center justify-between">
                  <div className="w-8 flex justify-center">
                    {index === 0 && (
                      <div className="cursor-pointer p-2" onClick={() => onDateNavigation?.("back")}>
                        <FontAwesomeIcon icon={faChevronLeft} />
                      </div>
                    )}
                  </div>

                  <div className="text-center flex-1">
                    <p className="rate-plan-day-number">{date.getDate()}</p>
                    <p className="rate-plan-day-of-week">{date.toLocaleDateString(undefined, { weekday: "long" })}</p>
                  </div>

                  <div className="w-8 flex justify-center">
                    {index === days.length - 1 && (
                      <div className="cursor-pointer p-2" onClick={() => onDateNavigation?.("forward")}>
                        <FontAwesomeIcon icon={faChevronRight} />
                      </div>
                    )}
                  </div>
                </div>
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {ratePlans.map(plan =>
            plan.units.map((unit, unitIndex) => {
              return (
                <tr key={`${plan.rate_plan_id}-${unit.unit_type_id}`}>
                  <td>
                    <div className="rate-plan-unit-type-container">
                      <div>
                        <p className="rate-plan-name">{plan.rate_plan_name}</p>
                        <p className="unit-type-name">
                          {unitTypes?.find(ut => ut.id === unit.unit_type_id)?.title ?? "Unknown Unit"}
                        </p>
                      </div>

                      <div className="">
                        {/* <FontAwesomeIcon icon={faFilePen} /> */}
                        <MultipleActionDropdown
                          alignment="left"
                          options={[
                            {
                              type: "handler",
                              label: t("Adjust prices"),
                              handler: () => handleEditAllPrices(plan, unit),
                              icon: "arrow-left-arrow-right",
                            },
                            {
                              type: "handler",
                              label: t("Close on arrival"),
                              handler: () => handleCloseAllOnArrival(plan, unit),
                              icon: "circle-arrow-left",
                            },
                            {
                              type: "handler",
                              label: t("Close on departure"),
                              handler: () => handleCloseAllOnDeparture(plan, unit),
                              icon: "circle-arrow-right",
                            },
                            {
                              type: "handler",
                              label: t("Set length of stay"),
                              handler: () => handleSetAllLengthOfStayOpen(plan, unit),
                              icon: "clock",
                            },
                          ]}
                          disabled={false}
                        >
                          <FontAwesomeIcon icon={faEllipsisVertical} className="ml-2 cursor-pointer" />
                        </MultipleActionDropdown>
                      </div>
                    </div>
                  </td>

                  {days.map((day, dayIndex) => {
                    const priceObj = unit.prices[dayIndex];
                    const rate = priceObj?.price ?? 0;
                    const isAfterEnd = endingDate ? day > endingDate : false;
                    const cellKey = `${unit.unit_type_id}-${dayIndex}`;
                    const isPlaceholder = !priceObj?.id;

                    return (
                      <td key={cellKey} className={`rate-cell ${isAfterEnd || isPlaceholder ? "disabled" : ""}`}>
                        {editingCell?.ratePlanId === plan.rate_plan_id &&
                        editingCell?.unitIndex === unitIndex &&
                        editingCell?.dayIndex === dayIndex ? (
                          <Input
                            className="rate-plan-input"
                            labelHidden
                            id={`rate-${plan.rate_plan_id}-${unit.unit_type_id}-${dayIndex}`}
                            value={editedValue}
                            autoFocus
                            onChange={e => {
                              setEditedValue(e.target.value);
                              if (saveTimeout.current) {
                                clearTimeout(saveTimeout.current);
                              }

                              saveTimeout.current = setTimeout(() => {
                                if (onRateChange && priceObj) {
                                  const parsed = parseFloat(e.target.value);
                                  if (!isNaN(parsed)) {
                                    onRateChange(plan.rate_plan_id, unitIndex, dayIndex, parsed, priceObj);
                                    setEditingCell(null);
                                  }
                                }
                              }, 2000);
                            }}
                            onBlur={() => {
                              if (editedValue !== rate.toString() && onRateChange && priceObj) {
                                const parsed = parseFloat(editedValue);
                                if (!isNaN(parsed)) {
                                  onRateChange(plan.rate_plan_id, unitIndex, dayIndex, parsed, priceObj);
                                }
                              }
                              setEditingCell(null);
                              if (saveTimeout.current) {
                                clearTimeout(saveTimeout.current);
                              }
                              setEditingCell(null);
                            }}
                            placeholder="Enter rate"
                          />
                        ) : (
                          <div className="rate-plan-cell-content">
                            <p></p>
                            <p
                              onClick={() => {
                                if (!isAfterEnd && !editingCell) {
                                  setEditingCell({
                                    ratePlanId: plan.rate_plan_id,
                                    unitIndex,
                                    dayIndex,
                                  });
                                  setEditedValue(rate.toString());
                                }
                              }}
                              className="rate-plan-price"
                            >
                              ${rate.toFixed(0)}
                            </p>

                            <div className="rate-plan-options">
                              <MultipleActionDropdown
                                alignment="right"
                                options={[
                                  // {
                                  //   type: "handler",
                                  //   label: t("Adjust prices"),
                                  //   handler: () => {
                                  //     if (!isAfterEnd && !editingCell) {
                                  //       setEditingCell({
                                  //         ratePlanId: plan.rate_plan_id,
                                  //         unitIndex,
                                  //         dayIndex,
                                  //       });
                                  //       setEditedValue(rate.toString());
                                  //     }
                                  //   },
                                  //   icon: "circle-arrow-left",
                                  // },
                                  {
                                    type: "handler",
                                    label: t("Close on arrival"),
                                    handler: () => handleCloseOnArrival(plan, unitIndex, dayIndex),
                                    icon: "circle-arrow-left",
                                    disabled: plan?.units?.[unitIndex]?.prices?.[dayIndex]?.closed_to_arrival,
                                  },
                                  {
                                    type: "handler",
                                    label: t("Close on departure"),
                                    handler: () => handleCloseOnDeparture(plan, unitIndex, dayIndex),
                                    icon: "circle-arrow-right",
                                    disabled: plan?.units?.[unitIndex]?.prices?.[dayIndex]?.closed_to_departure,
                                  },
                                  {
                                    type: "handler",
                                    label: t("Set length of stay"),
                                    handler: () => handleSetLengthOfStayOpen(plan, unitIndex, dayIndex),
                                    icon: "clock",
                                  },
                                ]}
                                disabled={false}
                              >
                                <FontAwesomeIcon icon={faEllipsisVertical} className="ml-2 cursor-pointer" />
                              </MultipleActionDropdown>
                            </div>
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            }),
          )}
        </tbody>
      </table>
    </div>
  );
};

export default RatePlanTable;
